import { __awaiter } from "tslib";
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { DefaultButton, MessageBar, MessageBarType, Persona, PersonaSize, PrimaryButton, Stack, Text, TextField, } from '@fluentui/react';
import DSEService from '../../../services/DSE.service';
const clampHours = (n) => isNaN(n) || n < 0 ? 0 : Math.round(n);
/** RAvail persistence-success code; anything else on a 200 means the write FAILED. */
const COSMOS_SUCCESS_CODE = 'C600';
/**
 * DSE capacity-hours editor (FM dashboard migration — walking-skeleton slice).
 *
 * Self-dependent React port of the GRM `$scope.open` → dseModal save flow
 * (`fmdashboard.controller.js`): view a resource's annual DSE capacity (contract) hours and
 * Save an update via the RAvail-owned `/api/ce/*` endpoints. No FXP shell needed to render.
 */
export const DSECapacityHours = React.forwardRef(({ alias, resourceName, initialHours, hours, onHoursChange, onSaved, onCancel, readOnly = false, readOnlyMessage = 'Capacity hours cannot be edited while sample data is shown.', dseService, showHeading = true, showPersona = true, capacityValueEstablished, capacityErrorMessage, onSavingChange, showActions = true, }, ref) => {
    // Resolve the FXP-backed singleton LAZILY (first actual call), not at render: `new
    // RAvailUrlProvider()` reads `window.FxpAppSettings`, so resolving eagerly makes the component
    // throw outside the shell — contradicting its "renders without FXP" contract.
    const svcRef = useRef(dseService !== null && dseService !== void 0 ? dseService : null);
    const getSvc = () => {
        if (!svcRef.current) {
            svcRef.current = DSEService.getSingleton();
        }
        return svcRef.current;
    };
    const [internalHours, setInternalHours] = useState(initialHours !== null && initialHours !== void 0 ? initialHours : 0);
    const [loading, setLoading] = useState(initialHours === undefined);
    const [saving, setSaving] = useState(false);
    const [internalValueEstablished, setInternalValueEstablished] = useState(initialHours !== undefined);
    const [readError, setReadError] = useState(undefined);
    const [message, setMessage] = useState(null);
    // Controlled/uncontrolled: when the host passes `hours`, IT owns the value so the surrounding
    // panel can recompute capacity metrics live as the user types (parity of the legacy
    // `ng-change="updateMetrics()"` on the EDE Capacity Hours input, dseModal.controller.js:197-213).
    const isControlled = hours !== undefined;
    const currentHours = isControlled ? hours : internalHours;
    const valueEstablished = capacityValueEstablished !== undefined
        ? capacityValueEstablished
        : internalValueEstablished;
    const applyHours = (v) => {
        if (!isControlled) {
            setInternalHours(v);
        }
        onHoursChange === null || onHoursChange === void 0 ? void 0 : onHoursChange(v);
    };
    const activeAliasRef = useRef(alias);
    const saveRequestRef = useRef(0);
    useEffect(() => {
        activeAliasRef.current = alias;
        saveRequestRef.current += 1;
        setSaving(false);
        setMessage(null);
        setReadError(undefined);
        if (initialHours !== undefined) {
            setInternalHours(initialHours);
        }
        setInternalValueEstablished(capacityValueEstablished !== undefined
            ? capacityValueEstablished
            : initialHours !== undefined);
        onSavingChange === null || onSavingChange === void 0 ? void 0 : onSavingChange(false);
    }, [
        alias,
        initialHours,
        capacityValueEstablished,
        onSavingChange,
    ]);
    useEffect(() => {
        if (initialHours !== undefined ||
            capacityValueEstablished !== undefined) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        (() => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            try {
                const resp = yield getSvc().getDSECapacityHours([alias]);
                if ((resp === null || resp === void 0 ? void 0 : resp.responseCode) !== 'G600') {
                    throw new Error((resp === null || resp === void 0 ? void 0 : resp.statusMessage) ||
                        'Capacity-hours service did not return a successful G600 response.');
                }
                const record = (_a = resp === null || resp === void 0 ? void 0 : resp.records) === null || _a === void 0 ? void 0 : _a.find((r) => { var _a; return ((_a = r.resourceAlias) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === alias.toLowerCase(); });
                if (!record) {
                    if (!cancelled) {
                        applyHours(0);
                        setInternalValueEstablished(true);
                        setReadError(undefined);
                    }
                    return;
                }
                if (typeof record.contractHours !== 'number' ||
                    !isFinite(record.contractHours)) {
                    throw new Error('Capacity hours returned for this resource were invalid.');
                }
                if (!cancelled) {
                    applyHours(record.contractHours);
                    setInternalValueEstablished(true);
                    setReadError(undefined);
                }
            }
            catch (error) {
                if (!cancelled) {
                    setInternalValueEstablished(false);
                    setReadError(error instanceof Error
                        ? error.message
                        : 'Failed to load capacity hours.');
                }
            }
            finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }))();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        alias,
        initialHours,
        capacityValueEstablished,
        dseService,
    ]);
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        if (readOnly || !valueEstablished || saving) {
            return;
        }
        const saveAlias = alias;
        const saveRequest = ++saveRequestRef.current;
        setSaving(true);
        onSavingChange === null || onSavingChange === void 0 ? void 0 : onSavingChange(true);
        setMessage(null);
        const record = {
            resourceAlias: saveAlias,
            contractHours: clampHours(currentHours),
        };
        try {
            const response = yield getSvc().addUpdateDSECapacityHours([record]);
            const isCurrentSave = saveRequestRef.current === saveRequest &&
                activeAliasRef.current === saveAlias;
            // The RAvail API answers HTTP 200 for BOTH outcomes and distinguishes them by
            // `responseCode`: C600 = persisted, C601 = persistence FAILED
            // (CEController.cs:56-79, StatusConstants.cs:9-13). Treating any 200 as success silently
            // told the manager their capacity change had saved when it had not.
            const code = response === null || response === void 0 ? void 0 : response.responseCode;
            if (code !== COSMOS_SUCCESS_CODE) {
                if (isCurrentSave) {
                    setMessage({
                        type: MessageBarType.error,
                        text: (response === null || response === void 0 ? void 0 : response.statusMessage) ||
                            'Capacity-hours service returned an invalid save response.',
                    });
                }
                return;
            }
            if (isCurrentSave) {
                setMessage({
                    type: MessageBarType.success,
                    text: 'Successfully updated capacity hours.',
                });
                onSaved === null || onSaved === void 0 ? void 0 : onSaved(record.contractHours, saveAlias);
            }
        }
        catch (_a) {
            if (saveRequestRef.current === saveRequest &&
                activeAliasRef.current === saveAlias) {
                setMessage({
                    type: MessageBarType.error,
                    text: 'Failed to save capacity hours.',
                });
            }
        }
        finally {
            if (saveRequestRef.current === saveRequest &&
                activeAliasRef.current === saveAlias) {
                setSaving(false);
                onSavingChange === null || onSavingChange === void 0 ? void 0 : onSavingChange(false);
            }
        }
    });
    const blockingReadError = !loading && !valueEstablished
        ? capacityErrorMessage ||
            readError ||
            'Capacity hours are unavailable. Reload the dashboard before editing.'
        : undefined;
    const showCurrentValue = valueEstablished || initialHours !== undefined;
    React.useImperativeHandle(ref, () => ({ save: handleSave }));
    return (React.createElement(Stack, { tokens: { childrenGap: 16 }, styles: { root: { padding: 20, maxWidth: 440 } }, "data-testid": "dse-capacity-hours" },
        showHeading ? (React.createElement(Text, { variant: "xLarge", as: "h2" }, "DSE Capacity Hours")) : null,
        showPersona ? (React.createElement(Persona, { text: resourceName, secondaryText: alias, size: PersonaSize.size48 })) : null,
        message && (React.createElement(MessageBar, { messageBarType: message.type, "data-testid": "dse-message" }, message.text)),
        blockingReadError ? (React.createElement(MessageBar, { messageBarType: MessageBarType.error, "data-testid": "dse-capacity-read-error" }, blockingReadError)) : null,
        readOnly && (React.createElement(MessageBar, { messageBarType: MessageBarType.warning, "data-testid": "dse-readonly" }, readOnlyMessage)),
        React.createElement(TextField, { label: "EDE Capacity Hours", type: "number", min: 0, value: showCurrentValue ? String(currentHours) : '', disabled: loading || saving || readOnly || !valueEstablished, readOnly: readOnly || !valueEstablished, onChange: (_e, v) => applyHours(clampHours(Number(v))) }),
        showActions ? (React.createElement(Stack, { horizontal: true, tokens: { childrenGap: 8 } },
            React.createElement(PrimaryButton, { text: saving ? 'Saving…' : 'Save', onClick: handleSave, disabled: loading || saving || readOnly || !valueEstablished }),
            React.createElement(DefaultButton, { text: "Cancel", onClick: () => onCancel === null || onCancel === void 0 ? void 0 : onCancel(), disabled: saving }))) : null));
});
DSECapacityHours.displayName = 'DSECapacityHours';
export default DSECapacityHours;
//# sourceMappingURL=DSECapacityHours.js.map